import { useState } from 'react'
import { Box, Stack } from '@mui/material'
import type { ProductImage } from '@/content/imageMap'
import { colors } from '@/theme'
import { Media } from '@/components/ui/Media'
import { ComingSoonMedia } from '@/components/ui/ComingSoonMedia'

interface ProductGalleryProps {
  images: ProductImage[]
  alt: string
}

/** Product image gallery — large active image + selectable thumbnail strip. */
export function ProductGallery({ images, alt }: ProductGalleryProps) {
  const [active, setActive] = useState(0)

  if (images.length === 0) {
    return <ComingSoonMedia label="Imagery coming soon" />
  }

  const current = images[active]

  const onZoomMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    el.style.setProperty('--ox', `${((e.clientX - r.left) / r.width) * 100}%`)
    el.style.setProperty('--oy', `${((e.clientY - r.top) / r.height) * 100}%`)
  }

  return (
    <Stack spacing={2}>
      <Box
        onMouseMove={onZoomMove}
        sx={{
          position: 'relative',
          bgcolor: colors.surfaceAlt,
          borderRadius: 1,
          overflow: 'hidden',
          cursor: 'zoom-in',
          // Soft spotlight behind the product
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: 'radial-gradient(60% 55% at 50% 42%, rgba(212,175,55,0.12), transparent 70%)',
          },
          '& img': { position: 'relative', zIndex: 1, transformOrigin: 'var(--ox, 50%) var(--oy, 50%)', transition: 'transform 350ms ease' },
          '&:hover img': { transform: 'scale(1.6)' },
          '@media (hover: none)': { '&:hover img': { transform: 'none' } },
        }}
      >
        <Media src={current.src} srcSm={current.srcSm} sizes="(max-width:900px) 92vw, 560px" alt={alt} width={current.width} height={current.height} ratio={1} eager />
      </Box>
      {images.length > 1 && (
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }} useFlexGap>
          {images.map((img, i) => (
            <Box
              key={img.src}
              component="button"
              type="button"
              onClick={() => setActive(i)}
              aria-label={`View image ${i + 1}`}
              aria-current={i === active}
              sx={{
                p: 0,
                width: 72,
                height: 72,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                bgcolor: colors.surfaceAlt,
                border: `1px solid ${i === active ? colors.gold : colors.border}`,
                transition: 'border-color 300ms ease',
              }}
            >
              <Media src={img.srcSm} alt={`${alt} thumbnail ${i + 1}`} width={img.width} height={img.height} ratio={1} sizes="72px" />
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
